import { jsx } from 'react/jsx-runtime';
import { objKey, printUnsupportedPropWarning, isDefined } from '@ruter-ds-next/utils';
import classNames from 'classnames';
import { createElement } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var styles = {"btn":"btn___VKIr3","btn--disabled":"btn--disabled___XFIQA","btn--animated":"btn--animated___oN1fw","btn--skin--primary":"btn--skin--primary___BCAQt","btn--skin--secondary":"btn--skin--secondary___ExzLw","btn--skin--flat":"btn--skin--flat___WUHCi","btn--skin--destructive":"btn--skin--destructive___iRSit","btn--size--small":"btn--size--small___Ng-A4","btn--size--medium":"btn--size--medium___1wjVX","btn--size--large":"btn--size--large___LjmNW","btn--icon-only":"btn--icon-only___NgpgZ","start-icon":"start-icon___HKwqG","toggleable--visible":"toggleable--visible___Obb8s","end-icon":"end-icon___zjVej","children":"children___qlupY","toggleable":"toggleable___3XhD-","toggleable__inner":"toggleable__inner___FRa7k","icon-container":"icon-container___oMe24"};

var BtnSize;
(function (BtnSize) {
  BtnSize["DEFAULT"] = "DEFAULT";
  BtnSize["SMALL"] = "SMALL";
  BtnSize["MEDIUM"] = "MEDIUM";
  BtnSize["LARGE"] = "LARGE";
})(BtnSize || (BtnSize = {}));
const BtnSizeStyles = {
  [BtnSize.DEFAULT]: objKey(styles, 'btn--size--medium'),
  [BtnSize.SMALL]: objKey(styles, 'btn--size--small'),
  [BtnSize.MEDIUM]: objKey(styles, 'btn--size--medium'),
  [BtnSize.LARGE]: objKey(styles, 'btn--size--large')
};
var BtnSkin;
(function (BtnSkin) {
  BtnSkin["DEFAULT"] = "DEFAULT";
  BtnSkin["PRIMARY"] = "PRIMARY";
  BtnSkin["SECONDARY"] = "SECONDARY";
  BtnSkin["FLAT"] = "FLAT";
  BtnSkin["DESTRUCTIVE"] = "DESTRUCTIVE";
})(BtnSkin || (BtnSkin = {}));
const BtnSkinStyles = {
  [BtnSkin.DEFAULT]: objKey(styles, 'btn--skin--primary'),
  [BtnSkin.PRIMARY]: objKey(styles, 'btn--skin--primary'),
  [BtnSkin.SECONDARY]: objKey(styles, 'btn--skin--secondary'),
  [BtnSkin.FLAT]: objKey(styles, 'btn--skin--flat'),
  [BtnSkin.DESTRUCTIVE]: objKey(styles, 'btn--skin--destructive')
};
function Button(props) {
  const {
      animated = true,
      as = 'button',
      children,
      className,
      endIcon,
      ref_,
      size = BtnSize.DEFAULT,
      skin = BtnSkin.DEFAULT,
      startIcon
    } = props,
    rest = __rest(props, ["animated", "as", "children", "className", "endIcon", "ref_", "size", "skin", "startIcon"]);
  if (rest.variant) {
    printUnsupportedPropWarning('Button', 'variant', `Removed in 5.0.0. The component will handle this internally based on whether the 'startIcon' or 'endIcon' props are passed.`);
  }
  const cls = classNames(styles.btn, {
    [BtnSizeStyles[size]]: size,
    [BtnSkinStyles[skin]]: skin,
    [objKey(styles, 'btn--animated')]: animated,
    [objKey(styles, 'btn--disabled')]: props.disabled,
    [objKey(styles, 'btn--icon-only')]: isDefined(startIcon) && !isDefined(endIcon) && !isDefined(children) || !isDefined(startIcon) && isDefined(endIcon) && !isDefined(children)
  }, className);
  return /*#__PURE__*/createElement(as, Object.assign({
    className: cls,
    ref: ref_,
    tabIndex: as !== 'button' && props.disabled ? -1 : undefined
  }, rest), [isDefined(startIcon) && jsx("span", {
    className: classNames([objKey(styles, 'icon-container'), objKey(styles, 'start-icon')]),
    children: startIcon
  }, 0), isDefined(children) && jsx("span", {
    className: classNames(objKey(styles, 'children')),
    children: children
  }, 1), isDefined(endIcon) && jsx("span", {
    className: classNames([objKey(styles, 'icon-container'), objKey(styles, 'end-icon')]),
    children: endIcon
  }, 2)]);
}
Button.displayName = 'Button';
function ButtonToggleable(props) {
  const {
      children,
      className,
      visible
    } = props,
    rest = __rest(props, ["children", "className", "visible"]);
  return jsx("span", Object.assign({
    className: classNames(styles.toggleable, {
      [objKey(styles, 'toggleable--visible')]: visible
    }, className)
  }, rest, {
    children: jsx("span", {
      className: styles.toggleable__inner,
      children: children
    })
  }));
}
ButtonToggleable.displayName = 'Button.Toggleable';
/**
 * Use inside a `<Button />` to toggle the visibility of an element.
 */
Button.Toggleable = ButtonToggleable;

export { BtnSize, BtnSkin, Button };
